import { useSelector } from 'react-redux';
import {
    getCategoryTabId,
    getCampaignsForNewDealsToday,
    getTabs,
} from '@client/redux/modules/mylounge';
import { useVariant } from '@client/lib/assignments';
import { CategoryTabIds } from '@shared/constants';
import { Campaign } from '@shared/types/mylounge';
import { getCookie } from '@pegasus/utils';

const MAX_CAMPAIGNS_TO_DISPLAY = 8;

export interface UseNewDealsTodayReturn {
    isNewDealsTodayVariant: boolean;
    isNewDealsTabActive: boolean;
    newDealsCampaigns: Campaign[];
    campaignsForNewDealsTodayCarousel: Campaign[];
    isNewDealsToday: boolean;
}

/**
 * Custom hook for managing New Deals Today functionality
 *
 * @returns {UseNewDealsTodayReturn} Object containing:
 * - isNewDealsTodayVariant: Whether the NEW_DEALS_TODAY variant is active
 * - isNewDealsTabActive: Whether the NEW_DEALS_TODAY tab is currently active
 * - newDealsCampaigns: All campaigns starting today (full list, excluding early access and exclusive campaigns)
 * - campaignsForNewDealsTodayCarousel: Limited list of campaigns for display (max 8)
 * - isNewDealsToday: Whether to show the New Deals Today carousel on the All tab
 */
export const useNewDealsToday = (): UseNewDealsTodayReturn => {
    const isNewDealsLeapedUsers = useVariant('new_deals_leaped', 'variant_enable_new_deals');
    const isNewDealsDailyUsers = useVariant('new_deals_today', 'variant_enable_new_deals');
    const isNewDealsTodayVariant =
        isNewDealsLeapedUsers ||
        isNewDealsDailyUsers ||
        getCookie('isNewDealsTodayCarousel') === 'true';

    const categoryTabId = useSelector(getCategoryTabId);
    const tabs = useSelector(getTabs);
    const allNewDealsCampaigns = useSelector(getCampaignsForNewDealsToday) || [];

    if (!isNewDealsTodayVariant) {
        return {
            isNewDealsTodayVariant: false,
            isNewDealsTabActive: false,
            newDealsCampaigns: [],
            campaignsForNewDealsTodayCarousel: [],
            isNewDealsToday: false,
        };
    }

    // get All Campaigns THB campaign ID to filter out
    const allCampaignsThbCampaignId = tabs?.find(tab => tab.id === CategoryTabIds.ALL)?.thb?.id;
    const newDealsCampaigns = allNewDealsCampaigns.filter(
        campaign => campaign.campaignId !== allCampaignsThbCampaignId,
    );

    const isNewDealsTabActive =
        categoryTabId === CategoryTabIds.NEW_DEALS_TODAY && newDealsCampaigns.length > 0;
    const hasEnoughCampaignsForNewDealsTodayCarousel =
        newDealsCampaigns.length >= MAX_CAMPAIGNS_TO_DISPLAY;
    const campaignsForNewDealsTodayCarousel = newDealsCampaigns.slice(0, MAX_CAMPAIGNS_TO_DISPLAY);

    const isNewDealsToday =
        isNewDealsTodayVariant &&
        categoryTabId === CategoryTabIds.ALL &&
        hasEnoughCampaignsForNewDealsTodayCarousel;

    return {
        isNewDealsTodayVariant,
        isNewDealsTabActive,
        newDealsCampaigns,
        campaignsForNewDealsTodayCarousel,
        isNewDealsToday,
    };
};
